import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  useProductShippingMetadataList,
  useProductShippingMetadataByRetailer,
  useProductShippingMetadataMutations,
} from "../../../hooks/useProductShippingMetadata";
import ProductShippingMetadataModal from "./ProductShippingMetadataModal";
import ProductShippingMetadataViewModal from "./ProductShippingMetadataViewModal";

const ProductShippingMetadataTable: React.FC = () => {
  const { t } = useTranslation();
  const [retailerFilter, setRetailerFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<{ productId: string; retailer: string } | null>(null);
  const [viewItem, setViewItem] = useState<{ productId: string; retailer: string } | null>(null);

  const { data: allData, isLoading: allLoading, refetch: refetchAll } = useProductShippingMetadataList(
    searchTerm || undefined
  );
  const {
    data: filteredData,
    isLoading: filteredLoading,
    refetch: refetchFiltered,
  } = useProductShippingMetadataByRetailer(
    retailerFilter,
    searchTerm || undefined,
    retailerFilter !== "all"
  );

  const { deleteMetadata } = useProductShippingMetadataMutations();

  const isLoading = retailerFilter === "all" ? allLoading : filteredLoading;
  const data = retailerFilter === "all" ? allData : filteredData;
  const metadata = data?.data || [];

  const handleRefresh = () => {
    if (retailerFilter === "all") {
      refetchAll();
    } else {
      refetchFiltered();
    }
  };

  const handleCreate = () => {
    setEditMode(null);
    setModalOpen(true);
  };

  const handleEdit = (productId: string, retailer: string) => {
    setEditMode({ productId, retailer });
    setModalOpen(true);
  };

  const handleView = (productId: string, retailer: string) => {
    setViewItem({ productId, retailer });
    setViewModalOpen(true);
  };

  const handleDelete = async (productId: string, retailer: string) => {
    if (window.confirm(t("admin.shippingMetadata.confirmDelete", { productId, retailer }))) {
      await deleteMetadata.mutateAsync({ productId, retailer });
    }
  };

  const formatTimeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    return `${hours}h ago`;
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <Select
          value={retailerFilter}
          onChange={(e) => setRetailerFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">{t("admin.productCache.allRetailers")}</MenuItem>
          <MenuItem value="amazon">Amazon</MenuItem>
          <MenuItem value="walmart">Walmart</MenuItem>
        </Select>

        <TextField
          placeholder={t("admin.shippingMetadata.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
          }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          {t("admin.shippingMetadata.addMetadata")}
        </Button>

        <Tooltip title={t("admin.shippingMetadata.refresh")}>
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.productCache.productId")}</TableCell>
              <TableCell>{t("admin.productCache.retailer")}</TableCell>
              <TableCell>{t("admin.shippingMetadata.title")}</TableCell>
              <TableCell>{t("admin.shippingMetadata.price")}</TableCell>
              <TableCell>{t("admin.shippingMetadata.weightKg")}</TableCell>
              <TableCell>{t("admin.shippingMetadata.shippingCost")}</TableCell>
              <TableCell>{t("admin.shippingMetadata.source")}</TableCell>
              <TableCell>{t("admin.productCache.updated")}</TableCell>
              <TableCell align="right">{t("admin.productCache.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : metadata.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t("admin.shippingMetadata.noMetadataFound")}
                </TableCell>
              </TableRow>
            ) : (
              metadata.map((item: any) => (
                <TableRow key={`${item.productId}-${item.retailer}`}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                      {item.productId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.retailer}
                      size="small"
                      color={item.retailer === "amazon" ? "primary" : "secondary"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {item.productTitle || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.price ? `$${item.price.toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell>
                    {item.weightInKg?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell>
                    {item.fixedShippingPrice ? `$${item.fixedShippingPrice.toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.source} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(item.updatedAt).toLocaleString()}>
                      <Typography variant="body2">
                        {formatTimeAgo(item.updatedAt)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("admin.shippingMetadata.view")}>
                      <IconButton
                        size="small"
                        onClick={() => handleView(item.productId, item.retailer)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.edit")}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(item.productId, item.retailer)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(item.productId, item.retailer)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ProductShippingMetadataModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditMode(null);
        }}
        editMode={editMode}
      />

      {viewItem && (
        <ProductShippingMetadataViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setViewItem(null);
          }}
          productId={viewItem.productId}
          retailer={viewItem.retailer}
        />
      )}
    </Box>
  );
};

export default ProductShippingMetadataTable;

